import { Link, useLoaderData, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import CartButton from '@/components/features/cart/cart-button.tsx';
import Section from '@/components/layout/section.tsx';

const { VITE_LOCALE } = import.meta.env;

export default function Header() {
	const [isMenuOpen, setMenuOpen] = useState(false);

	const { i18n, t } = useTranslation();
	const dropdownRef = useRef<HTMLUListElement | null>(null);
	const data = useLoaderData({ from: '__root__' });
	const location = useLocation();

	const handleClickOutside = (event: any) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target) &&
			!event?.target.classList.contains('header-button')
		) {
			setMenuOpen(false);
		}
	};

	const isOrderHidden = location.pathname.startsWith('/order-service');

	const versionLink = `https://jervice.${VITE_LOCALE === 'pl' ? 'eu' : 'pl'}`;

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	return (
		<header className="sticky top-0 z-100 backdrop-blur-[8px] border-b border-neutral-200 bg-white/90">
			<Section className="flex items-center justify-between gap-4">
				<div className="flex items-start gap-3 max-sm:gap-2">
					<Link to="/">
						<img
							className="w-42 h-12.5 max-lg:w-40 max-lg:h-12 max-sm:w-26 max-sm:h-8"
							src={`/logo-${i18n.language}.svg`}
							alt="logo"
						/>
					</Link>
				</div>

				<div className="flex items-center gap-3 relative">
					<a
						href={`tel:${data?.contactPhone}`}
						className="ml-auto text-xl font-bold max-md:hidden text-neutral-600"
					>
						{data?.contactPhone}
					</a>

					{!isOrderHidden && (
						<Link
							to="/order-service"
							className="select-none flex items-center justify-center py-2 h-[44px] px-3 max-sm:hidden bg-black rounded-xl text-white goldman cursor-pointer transition-all hover:border-orange-600 active:scale-97"
						>
							<span className="text-lg max-md:text-md">{t('global.order_button_label')}</span>
						</Link>
					)}

					<CartButton />

					<a
						href={versionLink}
						target="_self"
						className="header-button flex text-2xl items-center justify-center w-[44px] h-[44px] shrink-0 border border-neutral-300 p-2 rounded-lg text-neutral-600 cursor-pointer transition-all hover:border-orange-600 active:scale-97"
					>
						{VITE_LOCALE === 'pl' ? '🇺🇸' : '🇵🇱'}
					</a>

					<button
						className="header-button w-[44px] h-[44px] flex items-center justify-center shrink-0 border border-neutral-300 p-2 rounded-lg text-neutral-600 cursor-pointer transition-all hover:border-orange-600 active:scale-97"
						onClick={() => setMenuOpen((prev) => !prev)}
					>
						{isMenuOpen ? <X /> : <Menu />}
					</button>

					<ul
						className="transition text-right absolute opacity-0 invisible data-[visible=true]:visible data-[visible=true]:opacity-100 w-fit top-15 right-0 p-4 text-neutral-700 font-normal flex flex-col gap-2 bg-white border border-neutral-300 rounded-xl whitespace-nowrap"
						data-visible={isMenuOpen}
						data-locale={VITE_LOCALE}
						ref={dropdownRef}
					>
						<li>
							<Link
								to="/"
								activeProps={{
									'data-active': 'true',
									className: 'underline text-[var(--primary)]',
								}}
								inactiveProps={{
									'data-active': 'false',
								}}
								className="hover:underline text-lg"
								onClick={() => setMenuOpen(false)}
							>
								{t('global.menu.home')}
							</Link>
						</li>
						<li>
							<Link
								to="/services"
								activeProps={{
									'data-active': 'true',
									className: 'underline text-[var(--primary)]',
								}}
								inactiveProps={{
									'data-active': 'false',
								}}
								className="hover:underline text-lg"
								onClick={() => setMenuOpen(false)}
							>
								{t('global.menu.services')}
							</Link>
						</li>
						<li>
							<Link
								to="/contacts"
								activeProps={{
									'data-active': 'true',
									className: 'underline text-[var(--primary)]',
								}}
								inactiveProps={{
									'data-active': 'false',
								}}
								className="hover:underline text-lg"
								onClick={() => setMenuOpen(false)}
							>
								{t('global.menu.contacts')}
							</Link>
						</li>
					</ul>
				</div>
			</Section>
		</header>
	);
}
